import { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';

const tabs = [
  {
    path: '/dashboard',
    label: '홈',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955a1.126 1.126 0 0 1 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  },
  {
    path: '/reserve',
    label: '예약',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  {
    path: '/schedules',
    label: '스케줄',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    ),
  },
  {
    path: '/reservations',
    label: '예약내역',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
      </svg>
    ),
  },
];

function getActiveIndex(pathname) {
  return tabs.findIndex((tab) =>
    pathname === tab.path ||
    (tab.path === '/schedules' && pathname.startsWith('/schedules')) ||
    (tab.path === '/reservations' && pathname.startsWith('/reservations'))
  );
}

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const activeIndex = getActiveIndex(location.pathname);

  // 슬라이딩 인디케이터 위치 계산
  useEffect(() => {
    if (activeIndex < 0 || !navRef.current) return;
    const buttons = navRef.current.querySelectorAll('[data-tab]');
    const btn = buttons[activeIndex];
    if (!btn) return;
    const navRect = navRef.current.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicatorStyle({
      left: btnRect.left - navRect.left + btnRect.width / 2 - 10,
      width: 20,
    });
  }, [activeIndex]);

  // View Transitions API로 페이지 전환
  const handleNavigate = (path, targetIndex) => {
    if (location.pathname === path) return;

    const direction = targetIndex > activeIndex ? 'forward' : 'backward';
    document.documentElement.dataset.navDirection = direction;

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        // flushSync로 React DOM 업데이트를 즉시 반영 — 없으면 View Transition이 변경을 감지 못함
        flushSync(() => {
          navigate(path);
        });
      });
    } else {
      navigate(path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-border shadow-nav z-50" style={{ viewTransitionName: 'bottom-nav' }}>
      <div
        ref={navRef}
        className="relative flex justify-around items-center h-16 max-w-lg mx-auto pb-[env(safe-area-inset-bottom)]"
      >
        {/* 슬라이딩 인디케이터 — view-transition-name으로 자동 위치 morph */}
        {activeIndex >= 0 && (
          <div
            className="absolute top-0 h-[2.5px] bg-tayo rounded-full"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              viewTransitionName: 'nav-indicator',
            }}
          />
        )}

        {tabs.map((tab, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={tab.path}
              data-tab={index}
              onClick={() => handleNavigate(tab.path, index)}
              className={`relative flex flex-col items-center justify-center w-full h-full transition-all duration-200 ${
                isActive ? 'text-tayo' : 'text-sub-text'
              }`}
            >
              <div className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}>
                {tab.icon}
              </div>
              <span className="text-[10px] mt-1 font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
